import { Shimmer } from '@components/Shimmer'
import { useUserOrders } from '@hooks/useUserOrders'
import { AppLayout } from '@layouts/AppLayout'
import { router } from 'expo-router'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getTokens, H2, Text, useMedia, YStack } from 'tamagui'
import { OrderRow } from './OrderRow'

function OrdersCard({ children }: { children: ReactNode }) {
  const media = useMedia()

  const paddingHorizontal = useMemo(() => {
    const sizes = getTokens().size
    return sizes[media.gtSm ? '$spacing-xsl' : '$spacing-xs'].val
  }, [media.gtSm])

  return (
    <YStack
      flex={1}
      paddingHorizontal={paddingHorizontal}
      paddingTop="$size.spacing-xxl"
      paddingBottom="$size.spacing-md"
    >
      <YStack
        flex={1}
        p="$size.spacing-xs"
        backgroundColor="$bg-surface"
        gap="$size.spacing-md"
        br="$border-radius-md"
        width="100%"
        $gtSm={{
          padding: '$size.spacing-md',
        }}
        $gtLg={{
          maxWidth: 1608,
          marginHorizontal: 'auto',
          padding: '$size.spacing-lg',
        }}
      >
        {children}
      </YStack>
    </YStack>
  )
}

export function OrdersList() {
  const { t } = useTranslation('Orders')
  const { orders, loading } = useUserOrders()

  const backRoute = {
    label: 'Página inicial',
    back: () => router.navigate('/'),
  }

  if (loading && orders.length === 0) {
    return (
      <AppLayout backRoute={backRoute}>
        <OrdersCard>
          <Shimmer width={146} height={26} />
          <YStack gap="$size.spacing-xs">
            <Shimmer width="100%" height={72} />
            <Shimmer width="100%" height={72} />
            <Shimmer width="100%" height={72} />
          </YStack>
        </OrdersCard>
      </AppLayout>
    )
  }

  return (
    <AppLayout backRoute={backRoute}>
      <OrdersCard>
        <H2 color="$text-primary" fontSize="$font-size-md" ff="$mulishRegular">
          {t('title')}
        </H2>

        {orders.length === 0 ? (
          <YStack
            flex={1}
            gap="$size.spacing-xxxs"
            justifyContent="center"
            alignItems="center"
          >
            <Text
              color="$text-primary"
              ff="$mulishSemiBold"
              fontSize="$font-size-sm"
              textAlign="center"
            >
              {t('empty.title')}
            </Text>
            <Text
              color="$text-secondary"
              ff="$mulishRegular"
              fontSize="$font-size-xs"
              textAlign="center"
            >
              {t('empty.description')}
            </Text>
          </YStack>
        ) : (
          <YStack gap="$size.spacing-xs">
            {orders.flatMap((order) => {
              // HET-3540: pedido com decomposição por item (order bump) = 1 linha POR ITEM
              const decomposed =
                order.items &&
                order.items.length > 1 &&
                order.items.some((item) => item.amountCents != null)

              return decomposed
                ? order.items!.map((item) => (
                    <OrderRow
                      key={`${order.id}-${item.cartItemId}`}
                      order={order}
                      item={item}
                    />
                  ))
                : [<OrderRow key={order.id} order={order} />]
            })}
          </YStack>
        )}
      </OrdersCard>
    </AppLayout>
  )
}
